import React from 'react';
import { motion } from 'framer-motion';

export function IntroSplash({
  username,
  onContinue
}: {
  username?: string | null;
  onContinue: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[70] bg-black text-white overflow-hidden">
      {/* Background animated shapes - modern art inspired */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-gradient-to-br from-fuchsia-500/40 via-purple-500/30 to-cyan-500/30 blur-3xl"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute -bottom-32 -right-28 w-[34rem] h-[34rem] rounded-[4rem] bg-gradient-to-tr from-emerald-500/30 via-teal-500/30 to-blue-500/20 blur-3xl"
          initial={{ scale: 0.8, rotate: -10, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ duration: 1.1, ease: 'easeOut', delay: 0.1 }}
        />
        <motion.div
          className="absolute top-1/3 left-[10%] w-64 h-64 bg-white/5 rounded-3xl"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.2 }}
        />
        <motion.div
          className="absolute bottom-1/4 right-[12%] w-40 h-40 bg-white/5 rounded-full"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.25 }}
        />
      </div>

      <div className="relative z-10 h-full w-full flex items-center justify-center p-6">
        <div className="max-w-xl w-full text-center">
          <motion.h1
            className="text-3xl md:text-5xl font-semibold tracking-tight">
            Welcome{username ? `, ${username}` : ''}.
          </motion.h1>
          <motion.p
            className="mt-4 text-base md:text-lg text-white/80">
            Preparing your immersive gallery experience.
          </motion.p>

          <div className="mt-10 flex items-center justify-center">
            <button
              onClick={onContinue}
              className="w-full md:w-60 px-5 py-3 rounded-lg bg-white text-black font-medium hover:bg-white/90 transition-colors">
              Continue
            </button>
          </div>

          <p className="mt-6 text-xs md:text-sm text-white/60">
            You can switch to 2D while 3D loads.
          </p>
        </div>
      </div>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          .blur-3xl { filter: none; }
        }
      `}</style>
    </div>
  );
}

export function LoadingOverlay({
  progress = 0,
  message = 'Loading 3D experience...',
  visible = true
}: {
  progress?: number;
  message?: string;
  visible?: boolean;
}) {
  if (!visible) return null;
  const pct = Math.round((progress || 0) * 100);

  return (
    <div className="absolute inset-0 z-[60] bg-black/80 text-white flex items-center justify-center">
      <div className="w-[min(90vw,520px)]">
        <div className="text-center mb-5">
          <p className="text-white/80 text-sm md:text-base">{message}</p>
        </div>
        <div className="w-full bg-white/10 rounded-full h-3 overflow-hidden">
          <div
            className="h-3 bg-white rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2 text-right text-xs text-white/70">{pct}%</div>
      </div>
    </div>
  );
}

export function BottomProgressBar({
  progress,
  message = 'Preparing 3D view in background...'
}: {
  progress: number;
  message?: string;
}) {
  const pct = Math.round((progress || 0) * 100);
  return (
    <div className="absolute left-0 right-0 bottom-0 z-[50] p-3 md:p-4 flex justify-center">
      <div className="w-[min(92vw,720px)] bg-black/70 text-white rounded-xl shadow-lg border border-white/10 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs md:text-sm text-white/85">{message}</span>
          <span className="text-xs text-white/70">{pct}%</span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
          <div
            className="h-2 bg-white rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export function SwitchTo3DModal({
  onConfirm,
  onDismiss
}: {
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="absolute inset-0 z-[65] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onDismiss} />
      <div className="relative z-10 w-[min(92vw,480px)] bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900">3D is ready</h3>
        <p className="mt-2 text-sm text-gray-600">
          The immersive 3D experience is ready. Switch now to explore your piece in 3D?
        </p>
        <div className="mt-5 flex gap-2 justify-end">
          <button
            onClick={onDismiss}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900">
            Stay in 2D
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-black text-white hover:bg-black/90">
            Switch to 3D
          </button>
        </div>
      </div>
    </div>
  );
}

export function Use2DWhileLoadingModal({
  onConfirm,
  onDismiss
}: {
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="absolute inset-0 z-[65] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onDismiss} />
      <div className="relative z-10 w-[min(92vw,520px)] bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900">3D is loading</h3>
        <p className="mt-2 text-sm text-gray-600">
          The 3D experience is still loading. Would you like to use the 2D view while it loads in the background?
        </p>
        <div className="mt-5 flex gap-2 justify-end">
          <button
            onClick={onDismiss}
            className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-900">
            Keep waiting
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg bg-black text-white hover:bg-black/90">
            Use 2D now
          </button>
        </div>
      </div>
    </div>
  );
}